import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { VictoryBar, VictoryChart, VictoryAxis, VictoryLabel, VictoryTooltip } from 'victory';

import { ChartContainer, NoDataContainer } from './LineBarChart.styles';

import {
    legendOptions,
    getLightningBar,
    getWindLine,
    getWindAxis,
    getBottomAxis,
    getLegend,
    keyToTZ,
} from './LineBarChart';

import { chartTheme } from '../../common/ChartTheme';
import { getDomainForDay } from '../Timeline/Timeline.helpers';
import { NoData } from '../NoData/NoData';
import { defaultVictoryVoronoiContainer } from '../_charts/Chart.defaults';
import { roundTo100, roundTo5 } from 'common/numbers';

const selectedLegendOptions = [legendOptions.observedLightning, legendOptions.windSpeed, legendOptions.totalGeneration];

const PARK_POWER_MAX = 200;
const TURBINE_POWER_MAX = 10;
const WIND_MAX = 20;

export const TotalGenerationAndWindChart = ({
    isSingleTurbine,
    chartWidth,
    chartHeight,
    totalGenerationData,
    observedLightningData,
    windSpeedData,
}) => {
    const barWidth = chartWidth / 50;
    const getDataMax = (data, MAX, rounder) =>
        Math.max(...data.map((item) => (rounder ? rounder(item.value) : item.value)), MAX);
    const totalGenerationMaximum = getDataMax(
        totalGenerationData,
        isSingleTurbine ? TURBINE_POWER_MAX : PARK_POWER_MAX,
        isSingleTurbine ? null : roundTo100
    );

    const windSpeedMaximum = getDataMax(windSpeedData, WIND_MAX, roundTo5);

    // It's possible for any of the data sets to be empty
    // Therefore set internalChartDay with any available data point
    let internalChartDay = moment();
    if (totalGenerationData[0]) {
        internalChartDay = moment.utc(totalGenerationData[0].key);
    } else if (windSpeedData[0]) {
        internalChartDay = moment.utc(windSpeedData[0].key);
    } else if (observedLightningData[0]) {
        internalChartDay = moment.utc(observedLightningData[0].key);
    }

    const thereIsNoData =
        (!windSpeedData || windSpeedData.length === 0) &&
        (!totalGenerationData || totalGenerationData.length === 0) &&
        (!observedLightningData || observedLightningData.length === 0);

    const chartPadding = { left: 40, right: 40, top: 40, bottom: 70 };

    const chartOffsetX = (chartWidth - chartPadding.left - chartPadding.right) / 48;

    return (
        <>
            <ChartContainer>
                <VictoryChart
                    domainPadding={{
                        x: [chartOffsetX, -chartOffsetX],
                    }}
                    width={chartWidth}
                    theme={chartTheme}
                    padding={chartPadding}
                    domain={{
                        x: getDomainForDay(internalChartDay),
                        y: [0, 1],
                    }}
                    containerComponent={defaultVictoryVoronoiContainer}>
                    {getLegend(selectedLegendOptions, chartWidth, chartHeight)}
                    <VictoryAxis
                        key="left"
                        name="total-generation-axis"
                        orientation="left"
                        axisLabelComponent={<VictoryLabel verticalAnchor="start" y={0} x={72} angle={0} />}
                        label="Total Generation [MWh]"
                        dependentAxis
                        tickFormat={(t) => {
                            return Math.floor(t * totalGenerationMaximum);
                        }}
                    />
                    {getWindAxis(chartWidth, windSpeedMaximum)}
                    {getBottomAxis(chartWidth, chartOffsetX, internalChartDay)}
                    {totalGenerationData.length > 0 && (
                        <VictoryBar
                            key="1"
                            name="total-generation-chart"
                            y={(d) => d.value / totalGenerationMaximum}
                            x={keyToTZ}
                            style={{
                                data: { width: barWidth },
                            }}
                            labels={({ datum }) =>
                                datum.value === null ? 'Data Unavailable' : `${Math.round(datum.value)} MWh`
                            }
                            labelComponent={<VictoryTooltip {...chartTheme.tooltip} />}
                            data={totalGenerationData}
                        />
                    )}
                    {getWindLine(windSpeedData, windSpeedMaximum)}
                    {getLightningBar(observedLightningData, chartWidth, chartPadding)}
                </VictoryChart>
            </ChartContainer>
            {thereIsNoData && (
                <NoDataContainer>
                    <NoData />
                </NoDataContainer>
            )}
        </>
    );
};

TotalGenerationAndWindChart.displayName = 'TotalGenerationAndWindChart';

TotalGenerationAndWindChart.propTypes = {
    totalGenerationData: PropTypes.array,
    windSpeedData: PropTypes.array,
    observedLightningData: PropTypes.array,
    chartWidth: PropTypes.number,
    chartHeight: PropTypes.number,
};

TotalGenerationAndWindChart.defaultProps = {
    chartWidth: 920,
    chartHeight: 240,
    totalGenerationData: [],
    windSpeedData: [],
    observedLightningData: [],
};
